import { useState, useCallback, useRef } from "react"
import { BrotherPrinterService, GroupData, GuestData, TakeawayLabelData } from "../../external/brotherPrinter"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

export enum PrintStatus {
    IDLE = "IDLE",
    PRINTING = "PRINTING",
    SUCCESS = "SUCCESS",
    ERROR = "ERROR",
    NOT_INSTALLED = "NOT_INSTALLED",
    PRINTER_NOT_FOUND = "PRINTER_NOT_FOUND",
}

interface UseLabelPrinterProps {
    folderName?: string
    onPrintComplete?: (success: boolean) => void
}

export function useLabelPrinter({ folderName, onPrintComplete }: UseLabelPrinterProps = {}) {
    const { l10n } = useAppLocalization()
    const [printStatus, setPrintStatus] = useState<PrintStatus>(PrintStatus.IDLE)
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [currentLabelIndex, setCurrentLabelIndex] = useState<number>(0)
    const [totalLabels, setTotalLabels] = useState<number>(0)
    const cancelPrintingRef = useRef<boolean>(false)

    const printGuestLabel = useCallback(
        async (guestData: GuestData) => {
            if (!BrotherPrinterService.checkExtensionInstalled()) {
                setPrintStatus(PrintStatus.NOT_INSTALLED)
                return
            }
            if (!guestData.visitorName || !guestData.hostName) {
                setPrintStatus(PrintStatus.ERROR)
                return
            }

            setPrintStatus(PrintStatus.PRINTING)
            setErrorMessage("")
            setCurrentLabelIndex(1)
            setTotalLabels(1)

            try {
                const success = await BrotherPrinterService.printGuestLabel(guestData, folderName)
                setPrintStatus(success ? PrintStatus.SUCCESS : PrintStatus.ERROR)
                if (!success) {
                    setErrorMessage(l10n.getString("label-printer-error-failed-guest-label"))
                }
                onPrintComplete?.(success)
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : undefined
                if (message === "No online printer found") {
                    setPrintStatus(PrintStatus.PRINTER_NOT_FOUND)
                    setErrorMessage(l10n.getString("label-printer-error-printer-not-found"))
                } else {
                    setPrintStatus(PrintStatus.ERROR)
                    setErrorMessage(message || l10n.getString("label-printer-error-unknown-guest-label"))
                }
                onPrintComplete?.(false)
            } finally {
                setCurrentLabelIndex(0)
            }
        },
        [folderName, onPrintComplete, l10n],
    )

    const printMultipleGuestLabels = useCallback(
        async (guestDataList: GuestData[]) => {
            if (!BrotherPrinterService.checkExtensionInstalled()) {
                setPrintStatus(PrintStatus.NOT_INSTALLED)
                return
            }
            if (!guestDataList.length) {
                setErrorMessage(l10n.getString("label-printer-error-no-labels"))
                setPrintStatus(PrintStatus.ERROR)
                return
            }

            setPrintStatus(PrintStatus.PRINTING)
            setErrorMessage("")
            setTotalLabels(guestDataList.length)
            cancelPrintingRef.current = false

            let failedCount = 0

            for (let i = 0; i < guestDataList.length; i++) {
                // Check if printing was cancelled
                if (cancelPrintingRef.current) {
                    setErrorMessage(l10n.getString("label-printer-error-cancelled"))
                    setPrintStatus(PrintStatus.ERROR)
                    onPrintComplete?.(false)
                    setCurrentLabelIndex(0)
                    return
                }

                const guestData = guestDataList[i]
                setCurrentLabelIndex(i + 1)

                if (!guestData.visitorName || !guestData.hostName) {
                    continue // Skip invalid data (e.g. group entries without a single visitor name)
                }

                try {
                    const success = await BrotherPrinterService.printGuestLabel(guestData, folderName)
                    if (!success) {
                        failedCount++
                    }
                } catch (error: unknown) {
                    const message = error instanceof Error ? error.message : undefined
                    if (message === "No online printer found") {
                        setPrintStatus(PrintStatus.PRINTER_NOT_FOUND)
                        setErrorMessage(l10n.getString("label-printer-error-printer-not-found"))
                        onPrintComplete?.(false)
                        setCurrentLabelIndex(0)
                        return
                    }
                    failedCount++
                }
            }

            const allSuccessful = failedCount === 0
            if (!allSuccessful) {
                // Aggregate into a single message instead of one toast per failed guest
                setErrorMessage(
                    l10n.getString("label-printer-error-failed-labels-summary", {
                        failed: failedCount,
                        total: guestDataList.length,
                    }),
                )
            }
            setPrintStatus(allSuccessful ? PrintStatus.SUCCESS : PrintStatus.ERROR)
            onPrintComplete?.(allSuccessful)

            setCurrentLabelIndex(0)
        },
        [folderName, onPrintComplete, l10n],
    )

    const printGroupLabels = useCallback(
        async (groupData: GroupData) => {
            if (!groupData.groupName || !groupData.hostName || groupData.groupSize < 1) {
                setErrorMessage(l10n.getString("label-printer-error-required-fields"))
                setPrintStatus(PrintStatus.ERROR)
                return
            }

            setPrintStatus(PrintStatus.PRINTING)
            setErrorMessage("")
            setTotalLabels(groupData.groupSize)

            try {
                const success = await BrotherPrinterService.printGroupLabels(groupData, folderName)
                setPrintStatus(success ? PrintStatus.SUCCESS : PrintStatus.ERROR)
                if (!success) {
                    setErrorMessage(l10n.getString("label-printer-error-failed-group-labels"))
                }
                onPrintComplete?.(success)
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : undefined
                if (message === "No online printer found") {
                    setPrintStatus(PrintStatus.PRINTER_NOT_FOUND)
                    setErrorMessage(l10n.getString("label-printer-error-printer-not-found"))
                } else {
                    setPrintStatus(PrintStatus.ERROR)
                    setErrorMessage(message || l10n.getString("label-printer-error-unknown-group-labels"))
                }
                onPrintComplete?.(false)
            } finally {
                setCurrentLabelIndex(0)
            }
        },
        [folderName, onPrintComplete, l10n],
    )

    const printMultipleTakeawayLabels = useCallback(
        async (labelDataList: TakeawayLabelData[]) => {
            if (!BrotherPrinterService.checkExtensionInstalled()) {
                setPrintStatus(PrintStatus.NOT_INSTALLED)
                return
            }
            if (!labelDataList.length) {
                setErrorMessage(l10n.getString("label-printer-error-no-labels"))
                setPrintStatus(PrintStatus.ERROR)
                return
            }

            setPrintStatus(PrintStatus.PRINTING)
            setErrorMessage("")
            setTotalLabels(labelDataList.length)

            try {
                const success = await BrotherPrinterService.printMultipleTakeawayLabels(labelDataList)
                setPrintStatus(success ? PrintStatus.SUCCESS : PrintStatus.ERROR)
                if (!success) {
                    setErrorMessage(l10n.getString("label-printer-error-failed-takeaway-labels"))
                }
                onPrintComplete?.(success)
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : undefined
                if (message === "No online printer found") {
                    setPrintStatus(PrintStatus.PRINTER_NOT_FOUND)
                    setErrorMessage(l10n.getString("label-printer-error-printer-not-found"))
                } else {
                    setPrintStatus(PrintStatus.ERROR)
                    setErrorMessage(message || l10n.getString("label-printer-error-unknown-takeaway-labels"))
                }
                onPrintComplete?.(false)
            } finally {
                setCurrentLabelIndex(0)
            }
        },
        [onPrintComplete, l10n],
    )

    const cancelPrinting = useCallback(() => {
        cancelPrintingRef.current = true
    }, [])

    const clearState = useCallback(() => {
        setPrintStatus(PrintStatus.IDLE)
        setErrorMessage("")
        setCurrentLabelIndex(0)
        setTotalLabels(0)
        cancelPrintingRef.current = false
    }, [])

    const isLoading = printStatus === PrintStatus.PRINTING
    const hasError = printStatus === PrintStatus.ERROR
    const isInstalledError = printStatus === PrintStatus.NOT_INSTALLED
    const isPrinterNotFound = printStatus === PrintStatus.PRINTER_NOT_FOUND
    const hasSuccess = printStatus === PrintStatus.SUCCESS

    return {
        //actions
        printGuestLabel,
        printMultipleGuestLabels,
        printGroupLabels,
        printMultipleTakeawayLabels,
        clearState,
        cancelPrinting,

        //state
        isInstalledError: isInstalledError,
        isPrinterNotFound,
        printStatus,
        printErrorMessage: errorMessage,
        isLoading,
        hasError,
        hasSuccess,
        currentLabelIndex,
        totalLabels,
    }
}
